// `avatar tools <enable|disable|list|add|remove>` — quản lý tool toggle.
//
// Tool toggle = gói tự-đủ trong pack (tools/<name>/) bật/tắt nguyên khối
// qua merge/rút manifest vào project settings.json. Khác hook hạ-tầng luôn-bật
// (scout-block, dev-rules-reminder nằm trong settings.json.tpl).
//
// Subcommands:
//   enable <name>   merge manifest hooks + deny vào settings.json + state enabled
//                    name="all" → bật HẾT tool available (delegate runAdd all:true)
//   disable <name>  rút đúng entry tool ra + state disabled
//                    name="all" → gỡ HẾT tool đang bật (delegate runRemove all:true)
//   list            bảng: tool | available | enabled | version
//   add             multi-select tool available để cài (UX, bọc enable)
//   remove          multi-select tool đang bật để gỡ (UX, bọc disable)
//
// Flags: --target <path>; add/remove thêm --all (chọn hết, auto khi non-TTY) + --yes.
// Idempotent: enable lại không nhân đôi; disable tool chưa bật → no-op.
// add/remove/enable-all/disable-all tái dùng enableToolByName/disableToolByName (DRY),
// logic bulk nằm ở ./tools-add-remove-runner.ts.
// Bulk (add/enable-all) best-effort: 1 tool skip (vd ai-orchestrator cần TTY picker
// riêng) không chặn tool khác — luôn exit 0, chỉ in hint gợi ý bật riêng.
// disable-all KHÔNG confirm thêm (giống remove --all — gõ 'all' là opt-in rõ ràng).

import type { Command } from "commander";
import { listAvailableTools } from "../lib/discover-pack-tools-and-defaults.js";
import { log } from "../lib/terminal-logger.js";
import { disableToolByName, enableToolByName } from "../lib/tool-enable-disable-orchestrator.js";
import { readToolState } from "../lib/tool-state-store.js";
import {
  type AddRemoveOptions,
  resolveWorkspace,
  runAdd,
  runRemove,
  type ToolOptions,
} from "./tools-add-remove-runner.js";

async function runList(opts: ToolOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const available = await listAvailableTools(ws);
  const state = await readToolState(ws);

  if (available.length === 0 && Object.keys(state.tools).length === 0) {
    log.dim("Không có tool nào (pack chưa có tools/, hoặc chưa sync). Chạy 'avatar sync'.");
    return;
  }

  // Union tên: tool có trong pack + tool từng ghi trong state.
  const names = [...new Set([...available, ...Object.keys(state.tools)])].sort();
  log.info("Tools:");
  log.plain(`  ${"NAME".padEnd(20)} ${"AVAILABLE".padEnd(10)} ${"ENABLED".padEnd(8)} VERSION`);
  for (const name of names) {
    const isAvailable = available.includes(name) ? "yes" : "no";
    const entry = state.tools[name];
    const enabled = entry?.enabled ? "yes" : "no";
    const version = entry?.version ?? "-";
    log.plain(`  ${name.padEnd(20)} ${isAvailable.padEnd(10)} ${enabled.padEnd(8)} ${version}`);
  }
}

export function registerToolsCommand(program: Command): void {
  const tools = program
    .command("tools")
    .description("Quản lý tool toggle (prompt-scoring, ...) bật/tắt nguyên khối");

  tools
    .command("enable <name>")
    .description(
      "Bật tool: merge manifest vào settings.json + ghi state. Dùng name='all' để bật hết tool available",
    )
    .option("--target <path>", "Workspace root (default: cwd)")
    .action(async (name: string, opts: ToolOptions) => {
      // 'all' là từ khoá đặc biệt bật-hết (KHÔNG tool nào trong pack tên 'all';
      // nếu về sau xuất hiện, tool đó cần đổi tên). Check TRƯỚC resolveWorkspace
      // + trước exit-check single-tool để bulk skip không làm sai exit code.
      if (name === "all") {
        await runAdd({ ...opts, all: true });
        return;
      }
      const ws = resolveWorkspace(opts);
      const ok = await enableToolByName(ws, name);
      if (!ok) process.exit(1);
    });

  tools
    .command("disable <name>")
    .description(
      "Tắt tool: rút đúng entry manifest ra khỏi settings.json + ghi state. Dùng name='all' để gỡ hết tool đang bật",
    )
    .option("--target <path>", "Workspace root (default: cwd)")
    .action(async (name: string, opts: ToolOptions) => {
      // Xem note 'all' ở action enable phía trên — cùng quy ước.
      if (name === "all") {
        await runRemove({ ...opts, all: true });
        return;
      }
      const ws = resolveWorkspace(opts);
      await disableToolByName(ws, name);
    });

  tools
    .command("list")
    .description("Liệt kê tool: available | enabled | version")
    .option("--target <path>", "Workspace root (default: cwd)")
    .action(async (opts: ToolOptions) => {
      await runList(opts);
    });

  tools
    .command("add")
    .description("Chọn (multi-select) tool available để cài")
    .option("--target <path>", "Workspace root (default: cwd)")
    .option("--all", "Cài hết tool available (bỏ qua prompt)")
    .option(
      "--tools <names>",
      "Danh sách tool phân tách bằng dấu phẩy, vd: prompt-scoring,telemetry",
    )
    .option("--yes", "Bỏ xác nhận")
    .action(async (opts: AddRemoveOptions) => {
      await runAdd(opts);
    });

  tools
    .command("remove")
    .description("Chọn (multi-select) tool đang bật để gỡ")
    .option("--target <path>", "Workspace root (default: cwd)")
    .option("--all", "Gỡ hết tool đang bật (bỏ qua prompt)")
    .option(
      "--tools <names>",
      "Danh sách tool phân tách bằng dấu phẩy, vd: prompt-scoring,telemetry",
    )
    .option("--yes", "Bỏ xác nhận")
    .action(async (opts: AddRemoveOptions) => {
      await runRemove(opts);
    });
}
